import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { messages } from '@fruitways/shared';
import {
  Button,
  EmptyState,
  FilterCard,
  Picker,
  SkeletonRows,
  type PickerOption,
} from '@/components';
import { AppFrame } from '@/app/AppFrame';
import { useAdminFilters } from '@/app/AdminFilters';
import { seasonOrder, weekRuns, weeksByVariety } from '@/hooks/useAdmin';
import { useOnline } from '@/hooks/useOnline';
import { InlineError, InlineNote, t, useAdminData } from './dataShared';
import '@/screens/Capture/capture.css';

/**
 * Every variety harvest week of the current season as a read-only table, in season order
 * (week 52, 53, then 1, 2 …), then by variety. The only way to change them is to import a new
 * Excel file, which replaces them all.
 */
export function HarvestWeeksScreen() {
  const nav = useNavigate();
  const { online } = useOnline();
  const { master, harvestWeeks, season, loading, loadError } = useAdminData();
  const filters = useAdminFilters();
  const [picker, setPicker] = useState<'variety' | 'week' | null>(null);

  // Season order and the per-variety week lists change only with the data, not with a filter
  // or a keystroke in the picker, so they are computed once per data change.
  const order = useMemo(() => seasonOrder(season), [season]);
  const byVariety = useMemo(() => weeksByVariety(harvestWeeks, season), [harvestWeeks, season]);
  const inSeason = (harvestWeeks ?? []).filter((h) => h.seasonId === season?.id);
  const items = inSeason
    .filter(
      (h) =>
        (!filters.hwVariety || h.varietyCode === filters.hwVariety) &&
        (filters.hwWeek === null || h.weekNumber === filters.hwWeek),
    )
    .sort(
      (a, b) =>
        (order.get(a.weekNumber) ?? 99) - (order.get(b.weekNumber) ?? 99) ||
        a.varietyCode.localeCompare(b.varietyCode),
    );
  const varietyCount = new Set(items.map((h) => h.varietyCode)).size;
  const filterOn = Boolean(filters.hwVariety) || filters.hwWeek !== null;

  const weeksWithRows = [...new Set(inSeason.map((h) => h.weekNumber))].sort(
    (a, b) => (order.get(a) ?? 99) - (order.get(b) ?? 99),
  );
  const varieties = master?.varieties.map((v) => v.code) ?? [];
  const varietyOptions: PickerOption<string | null>[] = [
    { value: null, label: t.filter.allVarieties, detail: t.filter.varieties(varieties.length) },
    ...varieties.map((code) => {
      const ws = byVariety.get(code) ?? [];
      return {
        value: code,
        label: code,
        detail: ws.length
          ? t.hw.weeksSummary(weekRuns(ws), ws.length)
          : t.hw.noHarvestLine(season?.label ?? ''),
      };
    }),
  ];
  const weekOptions: PickerOption<number | null>[] = [
    {
      value: null,
      label: t.filter.allWeeks,
      detail: t.filter.weeksWithHarvest(weeksWithRows.length),
    },
    ...weeksWithRows.map((w) => ({
      value: w,
      label: messages.common.weekN(w),
      detail: t.filter.varieties(inSeason.filter((h) => h.weekNumber === w).length),
    })),
  ];

  return (
    <AppFrame
      title={t.hw.title}
      subtitle={season ? messages.header.season(season.label) : undefined}
      onBack={() => nav('/data')}
      offlineMessage={messages.banner.offlineData}
    >
      <div className="fw-content" style={{ gap: 'var(--space-2)' }}>
        <div className="fw-filter-head">
          <span className="fw-label">{t.filter.label}</span>
          {filterOn && (
            <button
              type="button"
              className="fw-text-button"
              onClick={() => filters.set({ hwVariety: null, hwWeek: null })}
            >
              {t.filter.clear}
            </button>
          )}
        </div>
        <FilterCard
          rows={[
            {
              key: 'variety',
              icon: 'leaf-outline',
              label: t.filter.variety,
              value: filters.hwVariety ?? t.filter.allVarieties,
              active: Boolean(filters.hwVariety),
              onClick: () => setPicker('variety'),
            },
            {
              key: 'week',
              icon: 'calendar-outline',
              label: t.filter.week,
              value:
                filters.hwWeek !== null ? messages.common.weekN(filters.hwWeek) : t.filter.allWeeks,
              active: filters.hwWeek !== null,
              onClick: () => setPicker('week'),
            },
          ]}
        />
        {loadError && <InlineError>{loadError}</InlineError>}
        {loading && !loadError && <SkeletonRows count={6} showSecondary={false} />}
        {!loading && (
          <span className="fw-secondary fw-tnum" style={{ paddingTop: 'var(--space-1)' }}>
            {t.hw.count(items.length, varietyCount)}
          </span>
        )}
        {!loading && items.length === 0 && (
          <EmptyState
            icon="calendar-outline"
            title={filterOn ? t.hw.noMatchTitle : t.hw.emptyTitle(season?.label ?? '')}
          >
            {filterOn ? t.hw.noMatchBody : t.hw.emptyBody}
          </EmptyState>
        )}
        {items.length > 0 && (
          <div className="fw-rows fw-tnum fw-table--two" role="table" aria-label={t.hw.title}>
            <div className="fw-table__head" role="row">
              <span role="columnheader">{t.hw.week}</span>
              <span role="columnheader">{t.hw.variety}</span>
            </div>
            {items.map((h) => (
              <div key={h.id} className="fw-table__row" role="row">
                <span role="cell" className="fw-table__code">
                  {h.weekNumber}
                </span>
                <span role="cell">{h.varietyCode}</span>
              </div>
            ))}
          </div>
        )}
        {!loading && <InlineNote>{t.data.replaceNote}</InlineNote>}
      </div>
      <div className="fw-actions fw-actions--column">
        <Button
          icon="document-text-outline"
          disabled={!online || loading}
          onClick={() => nav('/data/harvest-weeks/import')}
        >
          {t.hw.importFile}
        </Button>
      </div>

      <Picker
        open={picker === 'variety'}
        title={t.filter.chooseVariety}
        options={varietyOptions}
        value={filters.hwVariety}
        closeLabel={messages.common.close}
        searchLabel={t.filter.search}
        searchPlaceholder={t.filter.searchVariety}
        noMatch={t.filter.noMatch}
        onClose={() => setPicker(null)}
        onPick={(code) => {
          filters.set({ hwVariety: code });
          setPicker(null);
        }}
      />
      <Picker
        open={picker === 'week'}
        title={t.filter.chooseWeek}
        options={weekOptions}
        value={filters.hwWeek}
        closeLabel={messages.common.close}
        searchLabel={t.filter.search}
        searchPlaceholder={t.filter.searchWeek}
        noMatch={t.filter.noMatch}
        onClose={() => setPicker(null)}
        onPick={(w) => {
          filters.set({ hwWeek: w });
          setPicker(null);
        }}
      />
    </AppFrame>
  );
}
